/* Client-side poster renderer — exports an event as a shareable PNG (Instagram post or story). 3 templates. */

export const POSTER_SIZES = {
  square: { w: 1080, h: 1080, label: "Instagram Post", note: "1080×1080" },
  story: { w: 1080, h: 1920, label: "Instagram Story", note: "1080×1920" },
};

export const POSTER_TEMPLATES = {
  prism: { label: "Prism", note: "Colourful glass + ribbon" },
  editorial: { label: "Editorial", note: "Clean type, framed art" },
  spotlight: { label: "Spotlight", note: "Full-bleed photo" },
};

const hash = (s = "") => { let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return h; };
const DISPLAY = '"Clash Display", "Bebas Neue", Impact, sans-serif';
const MONO = '"Space Mono", ui-monospace, monospace';

const loadImage = (src) =>
  new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

/* Remote banners are served through a same-origin proxy so the canvas can be exported. */
async function loadArtwork(event) {
  const raw = event?.banner || event?.image_url || "";
  if (!raw) return null;
  if (raw.startsWith("data:")) return loadImage(raw);
  if (event?.id) {
    const proxied = await loadImage(`${process.env.REACT_APP_BACKEND_URL}/api/events/${event.id}/banner-proxy`);
    if (proxied) return proxied;
  }
  return loadImage(raw);
}

const wrap = (ctx, text, maxW, upper = true) => {
  const src = upper ? String(text || "").toUpperCase() : String(text || "");
  const words = src.split(/\s+/).filter(Boolean);
  const lines = [];
  let cur = "";
  for (const w of words) {
    const t = cur ? `${cur} ${w}` : w;
    if (ctx.measureText(t).width > maxW && cur) { lines.push(cur); cur = w; } else cur = t;
  }
  if (cur) lines.push(cur);
  return lines;
};

const fitLines = (ctx, text, maxW, startFs, minFs, roomH, weight = 600) => {
  let fs = startFs, lines = [];
  for (let i = 0; i < 20; i++) {
    ctx.font = `${weight} ${fs}px ${DISPLAY}`;
    lines = wrap(ctx, text, maxW);
    if (lines.length * fs * 0.95 <= roomH || fs <= minFs) break;
    fs = Math.max(minFs, Math.round(fs * 0.91));
  }
  return { fs, lines, lh: Math.round(fs * 0.95) };
};

const eventBits = (event) => {
  const d = event?.date ? new Date(event.date) : null;
  const ok = d && !isNaN(d);
  const tiers = Array.isArray(event?.tiers) ? event.tiers.filter((t) => Number(t?.price) > 0) : [];
  const from = tiers.length ? Math.min(...tiers.map((t) => Number(t.price))) : Number(event?.price) || 0;
  const loc = String(event?.location || "").trim();
  const cty = String(event?.city || "").trim();
  const org = String(event?.organizer_name || event?.organiser_name || event?.organizer || "").trim();
  return {
    day: ok ? String(d.getDate()).padStart(2, "0") : "",
    mon: ok ? d.toLocaleDateString("en-IN", { month: "short" }).toUpperCase() : "",
    yr: ok ? d.getFullYear() : "",
    cat: String(event?.category || "Event").toUpperCase(),
    place: (loc && cty && loc.toLowerCase().includes(cty.toLowerCase()) ? loc : [cty, loc].filter(Boolean).join(" · ")).toUpperCase() || "INDIA",
    entry: from > 0 ? `ENTRY FROM ₹${from}` : "FREE ENTRY",
    doors: event?.time ? `DOORS ${String(event.time).toUpperCase()}` : "",
    by: org ? `PRESENTED BY ${org.toUpperCase()}` : "",
    url: event?.slug ? `EVENTOM.ONLINE/E/${String(event.slug).toUpperCase()}` : "EVENTOM.ONLINE",
  };
};

/* Eventom lockup — lime bars + wordmark, drawn on every poster. */
const drawBrandMark = (ctx, x, y, h, color = "#CCFF00", label = "EVENTOM") => {
  const bar = Math.max(2, Math.round(h * 0.16));
  ctx.save();
  ctx.fillStyle = color;
  ctx.fillRect(x, y, bar, h);
  [0, 0.42, 0.84].forEach((f, i) => ctx.fillRect(x + bar * 1.6, y + h * f, h * (i === 1 ? 0.5 : 0.72), bar));
  ctx.font = `700 ${Math.round(h * 0.78)}px ${MONO}`;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText(label, x + bar * 1.6 + h * 0.9, y + h * 0.1);
  ctx.restore();
};

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const drawCover = (ctx, img, x, y, w, h, r = 0) => {
  ctx.save();
  if (r) roundRect(ctx, x, y, w, h, r); else ctx.rect(x, y, w, h);
  ctx.clip();
  const s = Math.max(w / img.width, h / img.height);
  const dw = img.width * s, dh = img.height * s;
  ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
  ctx.restore();
};

/* ---------------- TEMPLATE 1 — PRISM ---------------- */
function drawPrism(ctx, event, W, H, img) {
  const b = eventBits(event);
  const pad = Math.round(W * 0.075);
  ctx.fillStyle = "#05050A";
  ctx.fillRect(0, 0, W, H);
  const glow = (cx, cy, rad, color) => {
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, rad);
    g.addColorStop(0, color); g.addColorStop(1, "rgba(5,5,10,0)");
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
  };
  glow(W * 0.1, H * 0.05, W * 0.9, "rgba(138,43,226,0.55)");
  glow(W * 0.95, H * 0.18, W * 0.65, "rgba(0,229,255,0.28)");
  glow(W * 0.7, H * 1.02, W * 0.85, "rgba(255,0,127,0.34)");

  ctx.textBaseline = "top";
  ctx.textAlign = "left";
  ctx.fillStyle = "#CCFF00";
  ctx.font = `700 ${Math.round(W * 0.023)}px ${MONO}`;
  ctx.fillText(b.cat, pad, pad + Math.round(W * 0.03));
  if (b.day) {
    ctx.textAlign = "right";
    ctx.fillStyle = "#fff";
    ctx.font = `600 ${Math.round(W * 0.1)}px ${DISPLAY}`;
    ctx.fillText(b.day, W - pad, pad);
    ctx.font = `700 ${Math.round(W * 0.021)}px ${MONO}`;
    ctx.fillText(`${b.mon} ${b.yr}`, W - pad, pad + Math.round(W * 0.1));
  }

  const artTop = pad + Math.round(W * 0.16);
  const artW = W - pad * 2;
  const metaH = Math.round(W * 0.09);
  const detailH = Math.round(W * 0.13);
  const artH = Math.min(
    Math.round(H * (H > W ? 0.5 : 0.42)),
    H - pad - artTop - metaH - detailH - Math.round(W * 0.2),
  );
  if (img) drawCover(ctx, img, pad, artTop, artW, artH, 34);
  roundRect(ctx, pad, artTop, artW, artH, 34);
  ctx.strokeStyle = "rgba(255,255,255,0.35)";
  ctx.lineWidth = 3;
  ctx.stroke();

  // iridescent ribbon under the artwork
  const rg = ctx.createLinearGradient(pad, 0, W - pad, 0);
  ["#8A2BE2", "#00E5FF", "#FF007F", "#CCFF00"].forEach((c, i, a) => rg.addColorStop(i / (a.length - 1), c));
  ctx.fillStyle = rg;
  roundRect(ctx, pad, artTop + artH + Math.round(W * 0.035), artW, 8, 4);
  ctx.fill();

  const bottom = H - pad;
  const textTop = artTop + artH + Math.round(W * 0.085);
  ctx.textAlign = "left";
  ctx.fillStyle = "#fff";
  const { fs, lines, lh } = fitLines(ctx, event?.title, artW, Math.round(W * 0.1), Math.round(W * 0.062),
    bottom - metaH - detailH - textTop);
  ctx.font = `600 ${fs}px ${DISPLAY}`;
  lines.forEach((ln, i) => ctx.fillText(ln, pad, textTop + i * lh));

  ctx.font = `700 ${Math.round(W * 0.023)}px ${MONO}`;
  const dy = bottom - metaH - detailH + Math.round(W * 0.02);
  ctx.fillStyle = "#CCFF00";
  ctx.fillText(b.entry, pad, dy);
  ctx.fillStyle = "rgba(255,255,255,0.75)";
  if (b.doors) ctx.fillText(b.doors, pad, dy + Math.round(W * 0.042));
  if (b.by) { ctx.fillStyle = "rgba(255,255,255,0.6)"; ctx.fillText(b.by.slice(0, 40), pad, dy + Math.round(W * 0.084)); }

  // CTA pill
  const cw = Math.round(W * 0.62), ch = Math.round(W * 0.085);
  roundRect(ctx, pad, bottom - ch, cw, ch, ch / 2);
  ctx.fillStyle = "#CCFF00";
  ctx.fill();
  ctx.fillStyle = "#05050A";
  ctx.font = `700 ${Math.round(W * 0.022)}px ${MONO}`;
  ctx.textBaseline = "middle";
  ctx.fillText("BOOK ON EVENTOM.ONLINE", pad + Math.round(W * 0.045), bottom - ch / 2);
  ctx.textBaseline = "top";
  drawBrandMark(ctx, W - pad - Math.round(W * 0.2), bottom - ch + Math.round(ch * 0.28), Math.round(W * 0.032), "#CCFF00");
}

/* ---------------- TEMPLATE 2 — EDITORIAL ---------------- */
const EDITORIAL = [
  { bg: "#0C0C0C", fg: "#F4F4F0", accent: "#CCFF00" },
  { bg: "#101826", fg: "#F4F4F0", accent: "#00E5FF" },
  { bg: "#1A0A16", fg: "#F4F4F0", accent: "#FF4D94" },
  { bg: "#141414", fg: "#F4F4F0", accent: "#8A2BE2" },
];

function drawEditorial(ctx, event, W, H, img) {
  const v = EDITORIAL[hash(event?.id || event?.title || "") % EDITORIAL.length];
  const b = eventBits(event);
  const pad = Math.round(W * 0.075);
  ctx.fillStyle = v.bg;
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = `${v.fg}12`;
  ctx.lineWidth = 1;
  for (let x = -H; x < W; x += 26) { ctx.beginPath(); ctx.moveTo(x, H); ctx.lineTo(x + H, 0); ctx.stroke(); }

  ctx.textBaseline = "top";
  ctx.textAlign = "left";
  ctx.fillStyle = v.accent;
  ctx.font = `700 ${Math.round(W * 0.023)}px ${MONO}`;
  ctx.fillText(b.cat, pad, pad + Math.round(W * 0.03));
  if (b.day) {
    ctx.textAlign = "right";
    ctx.fillStyle = v.fg;
    ctx.font = `600 ${Math.round(W * 0.1)}px ${DISPLAY}`;
    ctx.fillText(b.day, W - pad, pad);
    ctx.font = `700 ${Math.round(W * 0.021)}px ${MONO}`;
    ctx.fillText(`${b.mon} ${b.yr}`, W - pad, pad + Math.round(W * 0.1));
  }

  const bottom = H - pad;
  const metaH = Math.round(W * 0.075);
  const details = [b.doors, b.entry, b.by, `BOOK · ${b.url}`].filter(Boolean);
  const dlh = Math.round(W * 0.042);
  const detailH = details.length * dlh + Math.round(W * 0.03);
  const gap = Math.round(W * 0.055);
  const minFs = Math.round(W * 0.07);

  const artTop = pad + Math.round(W * 0.155);
  const artW = W - pad * 2;
  const maxArtH = Math.min(Math.round(H * (H > W ? 0.56 : 0.48)), bottom - artTop - gap - metaH - detailH - Math.round(minFs * 1.9));
  let artH = Math.min(maxArtH, Math.round(H * (H > W ? 0.44 : 0.4)));
  if (img) {
    artH = Math.min(maxArtH, Math.max(Math.round(H * 0.22), Math.round(artW * (img.height / img.width))));
    drawCover(ctx, img, pad, artTop, artW, artH);
  }
  ctx.strokeStyle = `${v.fg}33`;
  ctx.lineWidth = 2;
  ctx.strokeRect(pad, artTop, artW, artH);

  ctx.textAlign = "left";
  ctx.fillStyle = v.fg;
  const textTop = artTop + artH + gap;
  const { fs, lines, lh } = fitLines(ctx, event?.title, artW, Math.round(W * (H > W ? 0.105 : 0.095)), minFs, bottom - metaH - detailH - textTop);
  ctx.font = `600 ${fs}px ${DISPLAY}`;
  lines.forEach((ln, i) => ctx.fillText(ln, pad, textTop + i * lh));

  ctx.font = `700 ${Math.round(W * 0.023)}px ${MONO}`;
  ctx.fillStyle = `${v.fg}b8`;
  const dy = bottom - metaH - details.length * dlh - Math.round(W * 0.01);
  details.forEach((t, i) => ctx.fillText(t.slice(0, 46), pad, dy + i * dlh));

  const y = bottom - metaH + Math.round(W * 0.012);
  ctx.strokeStyle = `${v.fg}47`;
  ctx.beginPath(); ctx.moveTo(pad, y); ctx.lineTo(W - pad, y); ctx.stroke();
  const metaY = y + Math.round(W * 0.024);
  ctx.fillStyle = v.fg;
  ctx.fillText(b.place.slice(0, 42), pad, metaY);
  drawBrandMark(ctx, W - pad - Math.round(W * 0.185), metaY - Math.round(W * 0.004), Math.round(W * 0.03), v.accent);
}

/* ---------------- TEMPLATE 3 — SPOTLIGHT ---------------- */
function drawSpotlight(ctx, event, W, H, img) {
  const b = eventBits(event);
  const pad = Math.round(W * 0.075);
  ctx.fillStyle = "#05050A";
  ctx.fillRect(0, 0, W, H);
  if (img) {
    drawCover(ctx, img, 0, 0, W, H);
    const g = ctx.createLinearGradient(0, H * 0.2, 0, H);
    g.addColorStop(0, "rgba(5,5,10,0)");
    g.addColorStop(0.55, "rgba(5,5,10,0.72)");
    g.addColorStop(1, "rgba(5,5,10,0.98)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "rgba(5,5,10,0.35)";
    ctx.fillRect(0, 0, W, Math.round(H * 0.22));
  } else {
    const g = ctx.createLinearGradient(0, 0, W, H);
    g.addColorStop(0, "#2A0835"); g.addColorStop(1, "#05050A");
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
  }

  // lime date block, top-left
  ctx.textBaseline = "top";
  const bw = Math.round(W * 0.22), bh = Math.round(W * 0.22);
  roundRect(ctx, pad, pad, bw, bh, 26);
  ctx.fillStyle = "#CCFF00";
  ctx.fill();
  ctx.fillStyle = "#05050A";
  ctx.textAlign = "center";
  ctx.font = `600 ${Math.round(bw * 0.52)}px ${DISPLAY}`;
  ctx.fillText(b.day || "--", pad + bw / 2, pad + Math.round(bh * 0.12));
  ctx.font = `700 ${Math.round(bw * 0.13)}px ${MONO}`;
  ctx.fillText(`${b.mon} ${b.yr}`, pad + bw / 2, pad + Math.round(bh * 0.68));

  ctx.textAlign = "right";
  ctx.fillStyle = "#fff";
  ctx.font = `700 ${Math.round(W * 0.023)}px ${MONO}`;
  ctx.fillText(b.cat, W - pad, pad + Math.round(W * 0.02));

  const bottom = H - pad;
  const ctaH = Math.round(W * 0.085);
  const detailH = Math.round(W * 0.075);
  ctx.textAlign = "left";
  ctx.fillStyle = "#fff";
  const { fs, lines, lh } = fitLines(ctx, event?.title, W - pad * 2, Math.round(W * 0.135), Math.round(W * 0.075), Math.round(H * 0.34));
  ctx.font = `600 ${fs}px ${DISPLAY}`;
  const titleTop = bottom - ctaH - detailH - Math.round(W * 0.06) - lines.length * lh;
  lines.forEach((ln, i) => ctx.fillText(ln, pad, titleTop + i * lh));

  ctx.font = `700 ${Math.round(W * 0.023)}px ${MONO}`;
  ctx.fillStyle = "#CCFF00";
  ctx.fillText(`${b.place.slice(0, 30)}`, pad, bottom - ctaH - detailH);
  if (b.by) { ctx.fillStyle = "rgba(255,255,255,0.72)"; ctx.fillText(b.by.slice(0, 34), pad, bottom - ctaH - detailH - Math.round(W * 0.04)); }
  ctx.fillStyle = "rgba(255,255,255,0.8)";
  ctx.textAlign = "right";
  ctx.fillText(b.entry, W - pad, bottom - ctaH - detailH);

  ctx.textAlign = "left";
  const cw = W - pad * 2;
  roundRect(ctx, pad, bottom - ctaH, cw, ctaH, ctaH / 2);
  const rg = ctx.createLinearGradient(pad, 0, pad + cw, 0);
  rg.addColorStop(0, "#8A2BE2"); rg.addColorStop(0.55, "#FF007F"); rg.addColorStop(1, "#FF5500");
  ctx.fillStyle = rg;
  ctx.fill();
  ctx.fillStyle = "#fff";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `700 ${Math.round(W * 0.024)}px ${MONO}`;
  ctx.fillText(`BOOK · ${b.url}`.slice(0, 44), W / 2, bottom - ctaH / 2);
  ctx.textBaseline = "top";
  drawBrandMark(ctx, W - pad - Math.round(W * 0.2), pad + Math.round(W * 0.075), Math.round(W * 0.032), "#CCFF00");
}

const RENDERERS = { prism: drawPrism, editorial: drawEditorial, spotlight: drawSpotlight };

async function render(event, sizeKey, template, withImage) {
  const { w: W, h: H } = POSTER_SIZES[sizeKey] || POSTER_SIZES.square;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  const img = withImage ? await loadArtwork(event) : null;
  (RENDERERS[template] || drawPrism)(ctx, event, W, H, img);
  return canvas;
}

/** Generate + download the poster PNG. Falls back to an art-free poster if artwork can't be exported. */
export async function downloadPoster(event, sizeKey = "square", template = "prism") {
  if (document.fonts?.ready) { try { await document.fonts.ready; } catch { /* noop */ } }
  let canvas = await render(event, sizeKey, template, true);
  let url;
  try {
    url = canvas.toDataURL("image/png");
  } catch {
    canvas = await render(event, sizeKey, template, false);
    url = canvas.toDataURL("image/png");
  }
  const slug = String(event?.slug || event?.title || "event").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 48);
  const a = document.createElement("a");
  a.href = url;
  a.download = `eventom-${slug}-${template}-${sizeKey}.png`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  return true;
}
